import React from 'react';
import type { Metro, State, MapStoreData, Store } from 'clinics/clinics';
import { type MilanBackgroundColors, type MilanGradientColors } from '@milanlaser/mlcomponents';
import { S32MapView, type S32MapViewProps } from './S32MapView/S32MapView';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { getLocationContext } from '../../../../utils/general/getLocationContext';
import { processBackgroundAndGradientColorMap } from '../../utils/builder.utils';
import { Typography } from '@milanlaser/mlcomponents';
import { ReviewsData } from '../../../../utils/ssr/types';
import type { Coordinates } from './S32MapView/Map/Map';
import { useBatchReviewsData } from '../../../../hooks/useReviewsData';
import { getMetroCenter } from '../../../../utils/ssr/utils/geo';

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state: BuilderState<T>;
    };
};

type S32MapViewSectionProps = BuilderProps<{
    metro: Metro;
    state: State;
    store: Store;
    mapData: MapStoreData[];
    model: 'clinic-page' | 'state-page' | 'metro-page';
    reviewsData?: ReviewsData;
    nearbyLocationReviews?: Record<string, ReviewsData | null>;
}> & {
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
    backgroundColor?: MilanBackgroundColors | 'None';
    backgroundGradient?: MilanGradientColors | 'None';
};

export function S32MapViewSection(props: S32MapViewSectionProps) {
    const {
        builderState,
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        backgroundColor,
        backgroundGradient,
    } = props;
    const { state, metro, store, mapData, model, nearbyLocationReviews: serverReviews } = builderState.state;

    const currentStoreAsMapData: MapStoreData = {
        salesforceValue: store.salesforceValue,
        marketingCity: store.marketingCity,
        address: store.address,
        addressCity: store.addressCity,
        state: store.state,
        zipCode: store.zipCode,
        geo: store.geo,
        urlPath: store.urlPath,
    };

    const orderedStores = store
        ? [currentStoreAsMapData, ...mapData.filter((s) => s.salesforceValue !== store.salesforceValue)]
        : mapData;

    // S32MapView is only used on state and metro pages - filter locations by state for initial review fetching
    const defaultDisplayedStores = React.useMemo(() => {
        return orderedStores.filter((s) => s.state === state.abbreviation);
    }, [orderedStores, state.abbreviation]);
    const initialSKUs = React.useMemo(
        () => defaultDisplayedStores.map((s) => s.salesforceValue),
        [defaultDisplayedStores]
    );
    const { reviewsData: nearbyLocationReviews, fetchReviewsForSKUs } = useBatchReviewsData(
        initialSKUs,
        serverReviews || {},
        false // Don't auto-fetch, we have server data
    );

    const locationName = getLocationContext(builderState);
    const titleOverride = `Find a Milan Laser Clinic in ${locationName}`;
    const locations: S32MapViewProps['locations'] = React.useMemo(() => {
        return orderedStores.map((currentStore) => {
            const storeReviews = nearbyLocationReviews?.[currentStore.salesforceValue];
            const rating = storeReviews
                ? {
                      value: storeReviews.average ? parseFloat(storeReviews.average) : 0,
                      reviewCount: storeReviews.total || 0,
                  }
                : undefined;

            return {
                name: currentStore.marketingCity,
                salesforceName: currentStore.salesforceValue,
                address: {
                    line1: currentStore.address,
                    line2: `${currentStore.addressCity || currentStore.marketingCity}, ${currentStore.state} ${currentStore.zipCode}`,
                },
                coords: [Number(currentStore.geo.lat), Number(currentStore.geo.long)] as Coordinates,
                href: currentStore.urlPath,
                id: `${currentStore.geo.lat}-${currentStore.geo.long}`,
                rating,
            };
        });
    }, [orderedStores, nearbyLocationReviews]);

    // On state pages `metro` is just the first metro in the state, so only metro pages get the metro-based sort.
    const isMetroPage = model === 'metro-page';
    const metroCenter = isMetroPage && metro?.stores?.length ? getMetroCenter(metro) : null;
    const sortOrigin = metroCenter ? ([metroCenter.lat, metroCenter.long] as Coordinates) : undefined;

    // Where the map starts: the center of the metro's clinics on metro pages (so a multi-clinic metro isn't anchored
    // on whichever clinic is listed first), the first clinic otherwise.
    const firstStoreGeo = metro?.stores?.[0]?.geo;
    const defaultCoordinates =
        sortOrigin ??
        (firstStoreGeo ? ([Number(firstStoreGeo.lat), Number(firstStoreGeo.long)] as Coordinates) : undefined);

    // Handler for when nearby locations change (user searches, clicks location, etc)
    const handleNearbyLocationsChange = React.useCallback(
        (nearbyLocs: S32MapViewProps['locations']) => {
            const skusToFetch = nearbyLocs.map((loc) => loc.salesforceName);
            fetchReviewsForSKUs(skusToFetch);
        },
        [fetchReviewsForSKUs]
    );

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const backgroundColorClass = processBackgroundAndGradientColorMap({
        backgroundColor: backgroundColor || 'None',
        gradientColor: backgroundGradient || 'None',
    });

    if (!metro?.stores?.length) {
        return (
            <div className={`${backgroundColorClass} ${topPaddingClasses} ${bottomPaddingClasses}`}>
                <div className="container text-center py-8">
                    <Typography tag="p" variant="h3-large" color="Grey">
                        No locations available
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div className={`${backgroundColorClass} ${topPaddingClasses} ${bottomPaddingClasses}`}>
            <div className="container">
                <S32MapView
                    title={titleOverride}
                    locations={locations}
                    defaultCoordinates={defaultCoordinates}
                    sortOrigin={sortOrigin}
                    currentStateName={state.name}
                    onNearbyLocationsChange={handleNearbyLocationsChange}
                />
            </div>
        </div>
    );
}
